    <p-toast></p-toast>
    <app-page-shell>
      <app-page-hero eyebrow="HR Onboarding" title="Onboarding Workspace" subtitle="Template-driven onboarding plans, activation blockers, and logistics-ready task lists.">
        <div actions>
          <button pButton type="button" icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" (click)="load()"></button>
        </div>
      </app-page-hero>

      @if (loading) {
        <p-progressBar mode="indeterminate"></p-progressBar>
      }

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-hr-list-tabs
        [tabs]="[
          { key: 'templates', label: 'Onboarding Templates', icon: 'fa-solid fa-list-check', badge: templates.length },
          { key: 'plans', label: 'Active Plans', icon: 'fa-solid fa-user-clock', badge: activePlans.length }
        ]"
        [(activeTab)]="activeListTab"
      ></app-hr-list-tabs>

      <section class="onboarding-grid">
        <app-page-data-panel [hidden]="activeListTab !== 'plans'" title="Active Onboarding Plans" subtitle="Open employee onboarding work generated from templates." [resultText]="activePlans.length + ' plans'">
          <p-table [value]="activePlans" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '920px' }">
            <ng-template pTemplate="header">
              <tr>
                <th>Employee</th>
                <th>Template</th>
                <th>Status</th>
                <th>Target</th>
                <th>Tasks</th>
                <th>Started</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-plan>
              <tr>
                <td><strong>{{ plan.employeeName || '-' }}</strong><br><small>{{ plan.employeeNumber || plan.employeeId }}</small></td>
                <td>{{ plan.templateName || '-' }}</td>
                <td><p-tag [value]="format(plan.planStatus)" [severity]="planSeverity(plan.planStatus)"></p-tag></td>
                <td>{{ plan.targetCompletionDate || '-' }}</td>
                <td>{{ completedTasks(plan) }} / {{ plan.tasks?.length || 0 }}</td>
                <td>{{ plan.startedAt | date:'medium' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="6">No active onboarding plans.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel [hidden]="activeListTab !== 'templates'" title="Template Coverage" subtitle="Default logistics templates cloned into the tenant and available for editing." [resultText]="templates.length + ' templates'">
          @if (canManageOnboarding) {
            <div actions><button pButton type="button" icon="fa-solid fa-plus" label="New Template" (click)="resetTemplate(); templateDialogVisible = true"></button></div>
          }
          <p-table [value]="templates" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header">
              <tr><th>Template</th><th>Category</th><th>Tasks</th><th>Status</th><th style="width: 5rem"></th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-template>
              <tr>
                <td><strong>{{ template.templateName }}</strong><br><small>{{ template.templateCode }}</small></td>
                <td>{{ format(template.workerCategory) }}</td>
                <td>{{ template.tasks?.length || 0 }}</td>
                <td><p-tag [value]="template.active ? 'Active' : 'Inactive'" [severity]="template.active ? 'success' : 'secondary'"></p-tag></td>
                <td>
                  @if (canManageOnboarding) {
                    <button pButton type="button" icon="pi pi-pencil" [rounded]="true" [text]="true" (click)="editTemplate(template)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="5">No onboarding templates configured.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </section>

      @if (canManageOnboarding) {
        <app-hr-action-dialog [(visible)]="templateDialogVisible" [title]="templateForm.templateId ? 'Edit Onboarding Template' : 'New Onboarding Template'" eyebrow="Onboarding Setup" subtitle="Maintain the task list used when onboarding starts for an employee." icon="fa-solid fa-list-check" [contextTitle]="templateForm.templateName || templateForm.templateCode || 'New template'" contextSubtitle="Tasks may be required, block activation, and belong to different owners.">
          <app-page-form-section icon="fa-solid fa-list-check" title="Template Builder" subtitle="Maintain onboarding task lists used when HR starts an employee onboarding plan.">
          <div class="template-form">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-barcode"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="templateCode" pInputText [(ngModel)]="templateForm.templateCode" />
                <label for="templateCode">Template code</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-heading"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="templateName" pInputText [(ngModel)]="templateForm.templateName" />
                <label for="templateName">Template name</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-users-gear"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="templateWorkerCategory" [options]="categoryOptions" [(ngModel)]="templateForm.workerCategory" [showClear]="true" styleClass="w-full"></p-select>
                <label for="templateWorkerCategory">Worker category</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-toggle-on"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="templateActive" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="templateForm.active" styleClass="w-full"></p-select>
                <label for="templateActive">Active</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup class="wide">
              <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="templateDescription" pTextarea [(ngModel)]="templateForm.description" rows="3"></textarea>
                <label for="templateDescription">Description</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>

          <div class="task-builder">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-code"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="taskCode" pInputText [(ngModel)]="taskDraft.taskCode" />
                <label for="taskCode">Task code</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-check-double"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="taskName" pInputText [(ngModel)]="taskDraft.taskName" />
                <label for="taskName">Task name</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-user-tag"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="taskOwner" [options]="ownerOptions" [(ngModel)]="taskDraft.ownerType" styleClass="w-full"></p-select>
                <label for="taskOwner">Owner</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="taskDueOffset" pInputText type="number" [(ngModel)]="taskDraft.dueOffsetDays" />
                <label for="taskDueOffset">Due offset days</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-file-shield"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="taskDocumentTypeCode" pInputText [(ngModel)]="taskDraft.documentTypeCode" />
                <label for="taskDocumentTypeCode">Document type code</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-ban"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="taskBlocksActivation" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="taskDraft.blocksActivation" styleClass="w-full"></p-select>
                <label for="taskBlocksActivation">Blocks activation</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup class="wide">
              <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="taskDescription" pTextarea [(ngModel)]="taskDraft.taskDescription" rows="3"></textarea>
                <label for="taskDescription">Task description</label>
              </p-iftaLabel>
            </p-inputGroup>
            <button pButton type="button" icon="pi pi-plus" label="Add Task" (click)="addTask()" [disabled]="!taskDraft.taskName"></button>
          </div>

          <p-table [value]="templateForm.tasks || []" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1120px' }">
            <ng-template pTemplate="header">
              <tr><th>Seq</th><th>Task</th><th>Owner</th><th>Required</th><th>Blocks</th><th>Document</th><th>Due</th><th style="width: 5rem"></th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-task let-index="rowIndex">
              <tr>
                <td>{{ task.sequenceNo || ((index + 1) * 10) }}</td>
                <td><strong>{{ task.taskName }}</strong><br><small>{{ task.taskDescription || task.taskCode || '-' }}</small></td>
                <td>{{ task.ownerType || 'HR' }}</td>
                <td><p-tag [value]="task.requiredTask === false ? 'Optional' : 'Required'" [severity]="task.requiredTask === false ? 'secondary' : 'info'"></p-tag></td>
                <td><p-tag [value]="task.blocksActivation === false ? 'No' : 'Yes'" [severity]="task.blocksActivation === false ? 'secondary' : 'warn'"></p-tag></td>
                <td>{{ task.documentTypeCode || '-' }}</td>
                <td>{{ task.dueOffsetDays ?? 3 }} days</td>
                <td><button pButton type="button" icon="pi pi-times" severity="danger" [rounded]="true" [text]="true" (click)="removeTask(index)"></button></td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="8">No tasks added to this template.</td></tr>
            </ng-template>
          </p-table>

          <div class="form-actions">
            <button pButton type="button" icon="pi pi-save" label="Save Template" (click)="saveTemplate()" [disabled]="!templateForm.templateCode || !templateForm.templateName"></button>
            <button pButton type="button" icon="pi pi-undo" label="Reset" severity="secondary" [outlined]="true" (click)="resetTemplate()"></button>
          </div>
          </app-page-form-section>
        </app-hr-action-dialog>
      }
    </app-page-shell>
